<script lang="ts"></script>
<script setup lang="ts">
import { useGlobalCriterias } from "@/api/vibration-criteria-query";
import AppDataTable from "@/components/common/AppDataTable.vue";
import AppDialog from "@/components/common/AppDialog.vue";
import { useQueryManager } from "@/stores/query-manager";
import { Alert, AlertSummary, Condition, Site, useApi } from "@soft-db/monitoring-api-client";
import {
	Button,
	Card,
	ColumnSelection,
	Progress,
	RowSelectionState,
	SoftOverlay,
	SoftRadialProgress,
	Stepper,
	StepperContent,
	makeColumnHelper,
	toggleSelection,
} from "@soft-db/ui";
import { useCreateAlert, useSiteAlerts, useUpdateAlert } from "../../../api/alert-query";
import { useSites } from "../../../api/site-query";
import { useDuplicateNameGenerator } from "../../../utils/duplicate-name-generator";
import { useConditionTypeLabel } from "../composable/condition-label";
import AlertConditionMapping from "./AlertConditionMapping.vue";
import AlertImportConfirmation from "./AlertImportConfirmation.vue";
import { AlertMapping, ConditionMapping } from "./types";

const props = defineProps<{
	siteId: string;
	open?: boolean;
}>();

const { t } = useI18n();

const openModel = useVModel(props, "open");

const Step = {
	SiteSelection: 0,
	AlertsSelection: 1,
	Mapping: 2,
	Confirmation: 3,
	Import: 4,
} as const;
const step = ref<number>(Step.SiteSelection);

const { sites, isLoading: sitesLoading } = useSites();

const sitesWithAlerts = computed(() =>
	(sites.value || []).filter((p) => !p.archived && p.alerts !== 0)
);

const { Column: SiteColumn } = makeColumnHelper<Site>();

const siteSelection = ref<RowSelectionState>({});
function siteClicked(site: Site) {
	siteSelection.value = toggleSelection(siteSelection.value, site.id, "single");
}

const selectedSite = computed(() => {
	const id = Object.keys(siteSelection.value)[0];
	if (!id) {
		return null;
	}

	return sites.value.find((s) => s.id === id) || null;
});

const { alerts } = useSiteAlerts(() => selectedSite.value?.id || "");

const { Column: ASColumn } = makeColumnHelper<AlertSummary>();

const selectedAlerts = ref<RowSelectionState>({});
const selectedAlertsCount = computed(() => Object.keys(selectedAlerts.value).length);

const importing = ref(false);

const alertsWithMappings = ref<AlertMapping[]>([]);
const alertsWithoutMappings = ref<Alert[]>([]);

const hasVCMappings = computed(() => {
	return alertsWithMappings.value.some((a) =>
		a.conditionMappings.some((m) => m.type === "vibration_event")
	);
});

const hasWeeklyLimitMappings = computed(() => {
	return alertsWithMappings.value.some((a) =>
		a.conditionMappings.some((m) => m.type === "weekly_limit")
	);
});

function toggleAlertSelection(alert: AlertSummary) {
	selectedAlerts.value = toggleSelection(selectedAlerts.value, alert.id, "multiple");
}

const alertApi = useApi().alert();

const { globalVibrationCriterias } = useGlobalCriterias();

const globalVCIds = computed(() => {
	return new Set(globalVibrationCriterias.value.map((vc) => vc.id));
});

const preparing = ref(false);
async function prepareAlertsImport() {
	preparing.value = true;

	alertsWithMappings.value = [];
	alertsWithoutMappings.value = [];

	try {
		const ids = Object.keys(selectedAlerts.value);

		const alerts: Alert[] = [];
		for (const id of ids) {
			alerts.push(await alertApi.fetch(id));
		}

		for (const alert of alerts) {
			const mappings: ConditionMapping[] = [];

			for (const [idx, condition] of alert.conditions.entries()) {
				if (condition.type === "record") {
					continue;
				}

				const cMapping: ConditionMapping = {
					type: condition.type,
					index: idx,
					originalError: null,
					originalWarning: null,
					newError: null,
					newWarning: null,
				};

				if (condition.type === "vibration_event") {
					const { vibrationCriteriaError: error, vibrationCriteriaWarning: warning } = condition;
					if (!globalVCIds.value.has(error ?? "")) {
						cMapping.originalError = error || null;
					}

					if (!globalVCIds.value.has(warning ?? "")) {
						cMapping.originalWarning = warning || null;
					}
				}

				if (condition.type === "weekly_limit") {
					const { weeklyLimitError: error, weeklyLimitWarning: warning } = condition;

					cMapping.originalError = error || null;
					cMapping.originalWarning = warning || null;
				}

				// Nothing to map, skip it.
				if (cMapping.originalError == null && cMapping.originalWarning == null) {
					continue;
				}

				mappings.push(cMapping);
			}

			if (mappings.length > 0) {
				alertsWithMappings.value.push({
					alert,
					conditionMappings: mappings,
				});
			} else {
				alertsWithoutMappings.value.push(alert);
			}
		}
	} finally {
		preparing.value = false;
	}
}

const { alerts: currentSiteAlerts } = useSiteAlerts(() => props.siteId);

const nameGenerator = useDuplicateNameGenerator(
	computed(() => currentSiteAlerts.value?.map((a) => a.name) || [])
);

const createAlert = useCreateAlert();
const updateAlert = useUpdateAlert();

const queryManager = useQueryManager();

const alertsToImport = ref(0);
const alertsImported = ref(0);

async function executeImport() {
	alertsImported.value = 0;
	alertsToImport.value = alertsWithoutMappings.value.length + alertsWithMappings.value.length;

	await queryManager.chainInvalidation(async () => {
		for (const alert of alertsWithoutMappings.value) {
			alertsImported.value++;
			await importAlert(alert);
		}

		for (const alert of alertsWithMappings.value) {
			alertsImported.value++;

			// Apply mapping to alert
			for (const mapping of alert.conditionMappings) {
				const condition = alert.alert.conditions[mapping.index];

				// Should not happen...
				if (condition == null) {
					continue;
				}

				switch (condition.type) {
					case "vibration_event":
						condition.vibrationCriteriaError = mapping.newError || "";
						condition.vibrationCriteriaWarning = mapping.newWarning || "";
						break;
					case "weekly_limit":
						condition.weeklyLimitError = mapping.newError || "";
						condition.weeklyLimitWarning = mapping.newWarning || "";
						break;
				}
			}

			await importAlert(alert.alert);
		}
	});

	close();
}

async function importAlert(alert: Alert) {
	// prevent mutation of original object
	alert = { ...alert };
	alert.name = nameGenerator.generate(alert.name);

	const { id } = await createAlert.mutateAsync({
		site: props.siteId,
		alert: {
			name: alert.name,
		},
	});

	alert.conditions = alert.conditions.map<Condition>((c) => ({
		...c,
		id: "",
	}));
	alert.validityConditions = alert.validityConditions.map((c) => ({
		...c,
		id: "",
	}));

	await updateAlert.mutateAsync({
		id,
		data: {
			...alert,
			recipients: {
				// Some recipients may be from other space
				// The backend will filter them out.
				add: alert.recipients,
				remove: [],
			},
		},
	});
}

function clean() {
	importing.value = false;
	openModel.value = false;

	step.value = Step.SiteSelection;

	selectedAlerts.value = {};
	siteSelection.value = {};
}
watch(openModel, (value) => {
	if (!value) {
		// Prevent screen flash on close
		requestAnimationFrame(clean);
	}
});

const close = () => {
	openModel.value = false;
};

watch(step, (currentStep, previousStep) => {
	if (currentStep === Step.SiteSelection) {
		siteSelection.value = {};
		selectedAlerts.value = {};
	}

	const goingBack = previousStep - currentStep > 0;
	if (goingBack && currentStep === Step.Mapping && skipMapping.value) {
		step.value = Step.AlertsSelection;
	}
});

const skipMapping = computed(() => {
	return alertsWithMappings.value.length === 0;
});

async function nextAction() {
	switch (step.value) {
		case Step.SiteSelection:
			step.value = Step.AlertsSelection;
			break;

		case Step.AlertsSelection:
			step.value = Step.Mapping;
			await prepareAlertsImport();
			if (skipMapping.value) {
				step.value = Step.Confirmation;
			}
			break;

		case Step.Mapping:
			step.value = Step.Confirmation;
			break;

		case Step.Confirmation:
			step.value = Step.Import;
			await executeImport();
			break;
	}
}

function previousAction() {
	if (step.value === Step.SiteSelection) {
		return;
	}

	step.value -= 1;

	if (step.value === Step.Mapping && skipMapping.value) {
		step.value -= 1;
	}
}

const actionDisabled = computed(() => {
	switch (step.value) {
		case Step.SiteSelection:
			return selectedSite.value == null;

		case Step.AlertsSelection:
			return selectedAlertsCount.value === 0;

		case Step.Mapping:
			return preparing.value;

		case Step.Import:
			return true;
	}
});

const stepTitle = computed(() => {
	switch (step.value) {
		case Step.SiteSelection:
			return t("alert-settings.importSiteSelection");
		case Step.AlertsSelection:
			return t("alert-settings.importAlertSelection");
		case Step.Mapping:
			return t("alert-settings.importMapping");
		case Step.Confirmation:
			return t("alert-settings.importConfirmation");
		case Step.Import:
			return t("alert-settings.importImporting");
	}
});

const actionText = computed(() => {
	switch (step.value) {
		case Step.Confirmation:
		case Step.Import:
			return t(
				"alert-settings.importMultipleAlerts",
				{ n: selectedAlertsCount.value },
				{
					plural: selectedAlertsCount.value,
				}
			);

		default:
			return t("common.next");
	}
});

const conditionTypeLabel = useConditionTypeLabel();
</script>

<template>
	<AppDialog
		v-model="openModel"
		class="w-188"
		persistent
		:actionText="actionText"
		:actionDisabled="actionDisabled"
		:hideAction="step === Step.Import"
		@click:action="nextAction"
		@close="close"
	>
		<template #title>
			<div>
				<h2>{{ t("alert-settings.importAlert") }}</h2>
				<span class="text-sm font-normal opacity-80">{{ stepTitle }}</span>
			</div>
		</template>

		<template #content>
			<SoftOverlay v-if="sitesLoading" class="flex items-center justify-center" absolute>
				<SoftRadialProgress class="h-32 w-32" color="primary" thickness="4" />
			</SoftOverlay>
			<Stepper v-model="step" linear>
				<StepperContent :step="Step.SiteSelection">
					<AppDataTable
						layout="contained"
						:data="sitesWithAlerts"
						:defaultPageSize="10"
						@click:row="(row) => siteClicked(row.row.original)"
					>
						<template #columns>
							<ColumnSelection v-model:selection="siteSelection" selectionMode="single" />
							<SiteColumn
								field="name"
								:header="t('common.name')"
								enableGlobalFilter
								enableSorting
							/>
							<SiteColumn
								field="description"
								:header="t('common.description')"
								enableGlobalFilter
								enableSorting
							/>
							<SiteColumn
								field="alerts"
								:header="t('object.alert', 2)"
								enableGlobalFilter
								enableSorting
							/>
						</template>
					</AppDataTable>
				</StepperContent>
				<StepperContent :step="Step.AlertsSelection">
					<AppDataTable
						layout="contained"
						:data="alerts || []"
						@click:row="(row) => toggleAlertSelection(row.row.original)"
					>
						>
						<template #columns>
							<ColumnSelection v-model:selection="selectedAlerts" selectionMode="multiple" />
							<ASColumn field="name" :header="t('common.name')" enableGlobalFilter enableSorting />
							<ASColumn v-slot="{ getValue }" field="type" :header="t('common.type')" enableSorting>
								{{ conditionTypeLabel(getValue()) }}
							</ASColumn>
						</template>
					</AppDataTable>
				</StepperContent>
				<StepperContent :step="Step.Mapping">
					<div v-if="preparing">
						<Progress />
					</div>
					<div v-else-if="selectedSite" class="flex flex-col gap-4">
						<Card v-if="hasVCMappings || hasWeeklyLimitMappings" class="bg-base-100" content>
							<div v-if="hasVCMappings" class="text-warning font-semibold">
								{{ t("alert-settings.importNoCriteriaAccess", { n: selectedSite.name }) }}
							</div>

							<div v-if="hasWeeklyLimitMappings" class="text-warning font-semibold">
								{{ t("alert-settings.importNoWeeklyLimitAccess", { n: selectedSite.name }) }}
							</div>

							<span class="text-xs">
								{{ t("alert-settings.importNoMappingAccessHelp") }}
							</span>
						</Card>

						<div v-if="alertsWithMappings.length > 0" class="flex flex-col gap-2">
							<AlertConditionMapping
								v-for="alertMapping of alertsWithMappings"
								:key="alertMapping.alert.id"
								v-model="alertMapping.conditionMappings"
								:alert="alertMapping.alert"
								:fromSite="selectedSite.id"
								:toSite="siteId"
							/>
						</div>

						<!-- <div v-if="alertsWithoutMappings.length > 0" class="flex flex-col gap-2"> -->
						<!-- 	<template v-for="alert of alertsWithoutMappings" :key="alert.id"> -->
						<!-- 		<AlertConditionMapping -->
						<!-- 			:modelValue="[]" -->
						<!-- 			:alert="alert" -->
						<!-- 			:fromSite="selectedSite.id" -->
						<!-- 			:toSite="siteId" -->
						<!-- 		/> -->
						<!-- 	</template> -->
						<!-- </div> -->
					</div>
				</StepperContent>

				<StepperContent :step="Step.Confirmation">
					<div class="flex flex-col gap-4">
						<AlertImportConfirmation
							v-for="alert of alertsWithMappings"
							:key="alert.alert.id"
							:alert="alert.alert"
							:conditions="alert.conditionMappings"
							:fromSite="selectedSite!.id"
							:toSite="siteId"
						/>

						<AlertImportConfirmation
							v-for="alert of alertsWithoutMappings"
							:key="alert.id"
							:alert="alert"
							:conditions="[]"
							:fromSite="selectedSite!.id"
							:toSite="siteId"
						/>
					</div>
				</StepperContent>

				<StepperContent :step="Step.Import">
					<Progress :value="alertsImported" :max="alertsToImport" />
				</StepperContent>
			</Stepper>
		</template>

		<template #left-action>
			<Button variant="ghost" :disabled="step === 0" @click="previousAction">
				{{ $t("common.back") }}
			</Button>
		</template>
	</AppDialog>
</template>
